import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, TrendingDown, Minus, AlertCircle, BarChart3 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Sparklines de evolução das intenções de voto por candidato.
 * Dados 100% derivados do TSE (pesquisas_candidatos). Quando vazio, renderiza
 * mensagem transparente — NÃO inventa dados nem interpreta politicamente.
 */
const CandidateTrendPanel = ({ year, uf, cargo, municipio }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const params = { max_candidatos: 6, max_pontos: 20 };
        if (year && year !== 'all') params.year = year;
        if (uf && uf !== 'all') params.uf = uf;
        if (cargo && cargo !== 'all') params.cargo = cargo;
        if (municipio && municipio.trim()) params.municipio = municipio.trim();
        const resp = await axios.get(`${API}/api/radar/tendencias`, { params, timeout: 15000 });
        if (!cancelled) setData(resp.data);
      } catch {
        if (!cancelled) setData({ candidatos: [], status: 'erro' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [year, uf, cargo, municipio]);

  const status = data?.status;
  const candidatos = data?.candidatos || [];

  // Don't render anything while loading first batch (keeps Radar clean)
  if (loading && !data) return null;

  // Silent when no data — the page already shows the global TSE placeholder
  if (!candidatos.length && status !== 'ok') return null;

  return (
    <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md" data-testid="trend-panel">
      <div className="flex items-center gap-2 mb-1">
        <BarChart3 className="w-4 h-4 text-[#006030]" />
        <h3 className="text-sm font-bold text-[#0F1C2E]">Evolução nas pesquisas</h3>
        <Badge className="bg-[#F4F6F8] text-[#475569] text-[9px]">Apenas visualização (dados TSE)</Badge>
      </div>
      <p className="text-[11px] text-[#94A3B8] mb-3">
        Gráfico informativo sem interpretação política. Cada ponto é uma pesquisa oficialmente registrada.
      </p>

      {candidatos.length === 0 ? (
        <div className="py-6 text-center text-[11px] text-[#94A3B8] flex items-center justify-center gap-2">
          <AlertCircle className="w-4 h-4" />
          Nenhum candidato com dados suficientes para o filtro atual.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="trend-cards">
          {candidatos.map((c) => (
            <CandidateSparkCard key={c.nm_candidato} candidato={c} />
          ))}
        </div>
      )}
    </Card>
  );
};

const CandidateSparkCard = ({ candidato }) => {
  const chartData = useMemo(
    () => (candidato.series || []).map((p, i) => ({ i, v: p.v, dt: p.dt, instituto: p.instituto })),
    [candidato.series],
  );

  const delta = candidato.delta_pct;
  const deltaColor = delta == null ? 'text-[#94A3B8]' : delta > 0 ? 'text-[#006030]' : delta < 0 ? 'text-[#C62828]' : 'text-[#94A3B8]';
  const DeltaIcon = delta == null ? Minus : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const lineColor = delta == null ? '#94A3B8' : delta > 0 ? '#006030' : delta < 0 ? '#C62828' : '#475569';

  return (
    <div
      className="p-3 border border-[#E2E8F0] rounded-md bg-[#F9FAFB]"
      data-testid={`trend-card-${candidato.nm_candidato}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-[#0F1C2E] truncate">{candidato.nm_candidato}</p>
          {candidato.sg_partido && (
            <p className="text-[10px] text-[#94A3B8] uppercase">{candidato.sg_partido}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-black text-[#0F1C2E]">
            {candidato.latest != null ? `${candidato.latest.toFixed(1)}%` : 'n/d'}
          </p>
          <p className={`text-[10px] flex items-center justify-end gap-0.5 ${deltaColor}`}>
            <DeltaIcon className="w-3 h-3" />
            {delta == null ? 'n/d' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)} p.p.`}
          </p>
        </div>
      </div>
      <div className="h-12">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
            <Tooltip
              contentStyle={{ fontSize: 10, padding: '4px 6px', border: '1px solid #E2E8F0' }}
              formatter={(v) => [`${v}%`, 'Intenção']}
              labelFormatter={(i) => {
                const pt = chartData[i];
                return pt ? `${pt.dt}${pt.instituto ? ` · ${pt.instituto}` : ''}` : '';
              }}
            />
            <Line
              type="monotone"
              dataKey="v"
              stroke={lineColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[9px] text-[#94A3B8] mt-1">
        {candidato.n_pesquisas} pesquisa{candidato.n_pesquisas === 1 ? '' : 's'}
      </p>
    </div>
  );
};

export default CandidateTrendPanel;
